import { PageHeader } from "@/components/PageHeader";
import { useConfirm } from "@/contexts/ConfirmDialogContext";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import type { AdminPromoCoupon } from "@/types";
import {
  Badge,
  Button,
  Checkbox,
  CheckboxField,
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  Fieldset,
  Input,
  Label,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@a7y/tailwind-catalyst";
import * as Headless from "@headlessui/react";
import { PlusIcon, TrashIcon } from "@heroicons/react/16/solid";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Copy, Tag, X } from "lucide-react";
import { useState } from "react";

const DURATION_LABELS: Record<string, string> = {
  once: "Once",
  repeating: "Repeating",
  forever: "Forever",
};

export function PromoCodesPage() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [expandedCoupon, setExpandedCoupon] = useState<string | null>(null);
  const [isCreateCodeOpen, setIsCreateCodeOpen] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["promo-coupons"],
    queryFn: () => api.getPromoCoupons(),
  });

  const deleteCouponMutation = useMutation({
    mutationFn: (couponId: string) => api.deletePromoCoupon(couponId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["promo-coupons"] }),
  });

  const deleteCodeMutation = useMutation({
    mutationFn: ({ couponId, codeId }: { couponId: string; codeId: string }) =>
      api.deletePromoCode(couponId, codeId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["promo-coupons"] }),
  });

  const coupons: AdminPromoCoupon[] = data?.data ?? [];

  const toggleExpand = (couponId: string) => {
    setExpandedCoupon(expandedCoupon === couponId ? null : couponId);
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
  };

  return (
    <div className="px-4 py-0">
      <PageHeader title="Promo Codes" subtitle="Manage Stripe coupons and promotion codes">
        <Button color="emerald" onClick={() => setIsCreateOpen(true)}>
          <PlusIcon />
          Create Coupon
        </Button>
      </PageHeader>

      <Table dense striped>
        <TableHead>
          <TableRow>
            <TableHeader className="w-8" />
            <TableHeader>Name</TableHeader>
            <TableHeader>Type</TableHeader>
            <TableHeader>Discount</TableHeader>
            <TableHeader>Duration</TableHeader>
            <TableHeader>Redemptions</TableHeader>
            <TableHeader>Codes</TableHeader>
            <TableHeader>Status</TableHeader>
            <TableHeader>Expires</TableHeader>
            <TableHeader className="text-right">Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={10} className="text-center">
                <div className="mx-auto h-6 w-6 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 dark:border-zinc-600" />
              </TableCell>
            </TableRow>
          ) : coupons.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} className="py-8 text-center">
                <Tag className="mx-auto h-12 w-12 text-zinc-400" />
                <p className="mt-2 text-zinc-500">No coupons found</p>
                <p className="text-sm text-zinc-400">Create a coupon to get started</p>
              </TableCell>
            </TableRow>
          ) : (
            coupons.map((coupon) => (
              <>
                <TableRow key={coupon.id}>
                  <TableCell>
                    <button onClick={() => toggleExpand(coupon.id)} className="p-1 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300">
                      {expandedCoupon === coupon.id ? (
                        <ChevronDownIcon className="h-4 w-4" />
                      ) : (
                        <ChevronRightIcon className="h-4 w-4" />
                      )}
                    </button>
                  </TableCell>
                  <TableCell className="font-medium">{coupon.name}</TableCell>
                  <TableCell>
                    <Badge color={coupon.type === "extended_trial" ? "purple" : "blue"}>
                      {coupon.type === "extended_trial" ? "Trial" : "Discount"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {coupon.type === "extended_trial"
                      ? `${coupon.trialDays} days`
                      : `${coupon.percentOff}% off`}
                  </TableCell>
                  <TableCell>
                    {DURATION_LABELS[coupon.duration] ?? coupon.duration}
                    {coupon.duration === "repeating" && coupon.durationInMonths
                      ? ` (${coupon.durationInMonths}mo)`
                      : ""}
                  </TableCell>
                  <TableCell>
                    {coupon.timesRedeemed}
                    {coupon.maxRedemptions ? ` / ${coupon.maxRedemptions}` : ""}
                  </TableCell>
                  <TableCell>
                    <Badge color="zinc">{coupon.promoCodes.length}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge color={coupon.valid ? "green" : "red"}>
                      {coupon.valid ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-zinc-500">
                    {coupon.redeemBy ? formatDate(coupon.redeemBy) : "\u2014"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        plain
                        onClick={() => setIsCreateCodeOpen(coupon.id)}
                        title="Add promo code"
                      >
                        <PlusIcon />
                      </Button>
                      {coupon.valid && (
                        <Button
                          plain
                          onClick={async () => {
                            const ok = await confirm({
                              title: `Delete coupon "${coupon.name}"?`,
                              description: "This cannot be undone.",
                              confirmLabel: "Delete",
                              confirmColor: "red",
                            });
                            if (ok) deleteCouponMutation.mutate(coupon.id);
                          }}
                          title="Delete coupon"
                        >
                          <TrashIcon className="text-red-500" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>

                {/* Expanded promo codes row */}
                {expandedCoupon === coupon.id && (
                  <TableRow key={`${coupon.id}-codes`}>
                    <TableCell colSpan={10} className="bg-zinc-50 dark:bg-zinc-800/50">
                      <div className="px-4 py-3">
                        <div className="mb-2 flex items-center justify-between">
                          <h4 className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                            Promo Codes ({coupon.promoCodes.length})
                          </h4>
                          <Button plain onClick={() => setIsCreateCodeOpen(coupon.id)}>
                            <PlusIcon />
                            Add Code
                          </Button>
                        </div>
                        {coupon.promoCodes.length === 0 ? (
                          <p className="text-sm text-zinc-500">No promo codes yet. Add one to share with users.</p>
                        ) : (
                          <div className="space-y-2">
                            {coupon.promoCodes.map((code) => (
                              <div
                                key={code.id}
                                className="flex items-center justify-between rounded-lg border border-zinc-200 bg-white px-4 py-2 dark:border-zinc-700 dark:bg-zinc-800"
                              >
                                <div className="flex items-center gap-3">
                                  <code className="rounded bg-zinc-100 px-2 py-1 font-mono text-sm font-bold dark:bg-zinc-700">
                                    {code.code}
                                  </code>
                                  <button
                                    onClick={() => copyCode(code.code)}
                                    className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                                    title="Copy code"
                                  >
                                    <Copy className="h-4 w-4" />
                                  </button>
                                  <Badge color={code.active ? "green" : "zinc"}>
                                    {code.active ? "Active" : "Inactive"}
                                  </Badge>
                                  {code.firstTimeTransaction && (
                                    <Badge color="amber">First-time only</Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-4 text-sm text-zinc-500">
                                  <span>
                                    {code.timesRedeemed}
                                    {code.maxRedemptions ? ` / ${code.maxRedemptions}` : ""} used
                                  </span>
                                  {code.expiresAt && (
                                    <span>Expires {formatDate(code.expiresAt)}</span>
                                  )}
                                  {code.active && (
                                    <button
                                      onClick={async () => {
                                        const ok = await confirm({
                                          title: `Deactivate code "${code.code}"?`,
                                          confirmLabel: "Deactivate",
                                          confirmColor: "red",
                                        });
                                        if (ok) deleteCodeMutation.mutate({ couponId: coupon.id, codeId: code.id });
                                      }}
                                      className="text-red-500 hover:text-red-600"
                                      title="Deactivate code"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </>
            ))
          )}
        </TableBody>
      </Table>

      {/* Create Coupon Dialog */}
      <CreateCouponDialog
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
      />

      {/* Create Promo Code Dialog */}
      {isCreateCodeOpen && (
        <CreatePromoCodeDialog
          couponId={isCreateCodeOpen}
          open={true}
          onClose={() => setIsCreateCodeOpen(null)}
        />
      )}

      {/* Error messages */}
      {deleteCouponMutation.isError && (
        <div className="mt-4 rounded-lg bg-red-50 p-4 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400">
          Failed to delete coupon: {deleteCouponMutation.error?.message || "Unknown error"}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Create Coupon Dialog
// =============================================================================

function CreateCouponDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [type, setType] = useState<"percent_off" | "extended_trial">("percent_off");
  const [percentOff, setPercentOff] = useState("");
  const [trialDays, setTrialDays] = useState("");
  const [duration, setDuration] = useState<"once" | "repeating" | "forever">("once");
  const [durationInMonths, setDurationInMonths] = useState("");
  const [maxRedemptions, setMaxRedemptions] = useState("");
  const [redeemBy, setRedeemBy] = useState("");

  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof api.createPromoCoupon>[0]) => api.createPromoCoupon(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["promo-coupons"] });
      resetForm();
      onClose();
    },
  });

  const resetForm = () => {
    setName("");
    setType("percent_off");
    setPercentOff("");
    setTrialDays("");
    setDuration("once");
    setDurationInMonths("");
    setMaxRedemptions("");
    setRedeemBy("");
  };

  const isValid = (() => {
    if (!name.trim()) return false;
    if (type === "percent_off") {
      const pct = Number(percentOff);
      if (!percentOff || pct < 1 || pct > 100) return false;
      if (duration === "repeating") {
        const months = Number(durationInMonths);
        if (!durationInMonths || months < 1 || months > 36) return false;
      }
    }
    if (type === "extended_trial") {
      const days = Number(trialDays);
      if (!trialDays || days < 1 || days > 365) return false;
    }
    return true;
  })();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({
      name,
      type,
      percentOff: type === "percent_off" ? Number(percentOff) : undefined,
      trialDays: type === "extended_trial" ? Number(trialDays) : undefined,
      duration: type === "extended_trial" ? "once" : duration,
      durationInMonths: duration === "repeating" ? Number(durationInMonths) : undefined,
      maxRedemptions: maxRedemptions ? Number(maxRedemptions) : undefined,
      redeemBy: redeemBy || undefined,
    });
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Create Coupon</DialogTitle>
        <DialogBody>
          <Fieldset>
            <div data-slot="control" className="grid grid-cols-1 items-center gap-x-4 gap-y-6 sm:grid-cols-3">
              <Headless.Field className="grid grid-cols-subgrid sm:col-span-3">
                <Label>Name</Label>
                <Input
                  className="mt-3 sm:col-span-2 sm:mt-0"
                  value={name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                  placeholder='e.g., "ProductHunt Launch 2026"'
                  required
                />
              </Headless.Field>

              <Headless.Field className="grid grid-cols-subgrid sm:col-span-3">
                <Label>Coupon Type</Label>
                <Select
                  className="mt-3 sm:col-span-2 sm:mt-0"
                  value={type}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setType(e.target.value as typeof type)}
                >
                  <option value="percent_off">Percentage Discount</option>
                  <option value="extended_trial">Extended Trial</option>
                </Select>
              </Headless.Field>

              {type === "percent_off" && (
                <Headless.Field className="grid grid-cols-subgrid sm:col-span-3">
                  <Label>Percentage Off</Label>
                  <Input
                    className="mt-3 sm:col-span-2 sm:mt-0"
                    type="number"
                    min="1"
                    max="100"
                    value={percentOff}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPercentOff(e.target.value)}
                    placeholder="e.g., 50"
                    required
                  />
                </Headless.Field>
              )}

              {type === "extended_trial" && (
                <Headless.Field className="grid grid-cols-subgrid sm:col-span-3">
                  <Label>Trial Days</Label>
                  <Input
                    className="mt-3 sm:col-span-2 sm:mt-0"
                    type="number"
                    min="1"
                    max="365"
                    value={trialDays}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTrialDays(e.target.value)}
                    placeholder="e.g., 14"
                    required
                  />
                </Headless.Field>
              )}

              {type === "percent_off" && (
                <Headless.Field className="grid grid-cols-subgrid sm:col-span-3">
                  <Label>Duration</Label>
                  <Select
                    className="mt-3 sm:col-span-2 sm:mt-0"
                    value={duration}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setDuration(e.target.value as typeof duration)}
                  >
                    <option value="once">Once (first invoice only)</option>
                    <option value="repeating">Repeating (N months)</option>
                    <option value="forever">Forever</option>
                  </Select>
                </Headless.Field>
              )}

              {type === "percent_off" && duration === "repeating" && (
                <Headless.Field className="grid grid-cols-subgrid sm:col-span-3">
                  <Label>Months</Label>
                  <Input
                    className="mt-3 sm:col-span-2 sm:mt-0"
                    type="number"
                    min="1"
                    max="36"
                    value={durationInMonths}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDurationInMonths(e.target.value)}
                    placeholder="e.g., 3"
                    required
                  />
                </Headless.Field>
              )}

              <Headless.Field className="grid grid-cols-subgrid sm:col-span-3">
                <Label>Max Redemptions</Label>
                <Input
                  className="mt-3 sm:col-span-2 sm:mt-0"
                  type="number"
                  min="1"
                  value={maxRedemptions}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMaxRedemptions(e.target.value)}
                  placeholder="Unlimited"
                />
              </Headless.Field>

              <Headless.Field className="grid grid-cols-subgrid sm:col-span-3">
                <Label>Expires On</Label>
                <Input
                  className="mt-3 sm:col-span-2 sm:mt-0"
                  type="date"
                  value={redeemBy}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRedeemBy(e.target.value)}
                />
              </Headless.Field>
            </div>
          </Fieldset>

          {createMutation.isError && (
            <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400">
              {createMutation.error?.message || "Failed to create coupon"}
            </div>
          )}
        </DialogBody>
        <DialogActions>
          <Button plain onClick={onClose}>
            Cancel
          </Button>
          <Button color="emerald" type="submit" disabled={!isValid || createMutation.isPending}>
            {createMutation.isPending ? "Creating..." : "Create Coupon"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// =============================================================================
// Create Promo Code Dialog
// =============================================================================

function CreatePromoCodeDialog({
  couponId,
  open,
  onClose,
}: {
  couponId: string;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [code, setCode] = useState("");
  const [maxRedemptions, setMaxRedemptions] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [firstTimeTransaction, setFirstTimeTransaction] = useState(false);

  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof api.createPromoCode>[1]) =>
      api.createPromoCode(couponId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["promo-coupons"] });
      setCode("");
      setMaxRedemptions("");
      setExpiresAt("");
      setFirstTimeTransaction(false);
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({
      code: code.toUpperCase(),
      maxRedemptions: maxRedemptions ? Number(maxRedemptions) : undefined,
      expiresAt: expiresAt || undefined,
      firstTimeTransaction,
    });
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Generate Promo Code</DialogTitle>
        <DialogBody>
          <Fieldset>
            <div data-slot="control" className="grid grid-cols-1 items-center gap-x-4 gap-y-6 sm:grid-cols-3">
              <Headless.Field className="grid grid-cols-subgrid sm:col-span-3">
                <Label>Code</Label>
                <Input
                  className="mt-3 sm:col-span-2 sm:mt-0"
                  value={code}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ""))}
                  placeholder="e.g., LAUNCH50"
                  maxLength={30}
                  required
                />
              </Headless.Field>

              <Headless.Field className="grid grid-cols-subgrid sm:col-span-3">
                <Label>Max Redemptions</Label>
                <Input
                  className="mt-3 sm:col-span-2 sm:mt-0"
                  type="number"
                  min="1"
                  value={maxRedemptions}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMaxRedemptions(e.target.value)}
                  placeholder="Unlimited"
                />
              </Headless.Field>

              <Headless.Field className="grid grid-cols-subgrid sm:col-span-3">
                <Label>Expires On</Label>
                <Input
                  className="mt-3 sm:col-span-2 sm:mt-0"
                  type="date"
                  value={expiresAt}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setExpiresAt(e.target.value)}
                />
              </Headless.Field>

              <CheckboxField className="sm:col-span-3">
                <Checkbox
                  checked={firstTimeTransaction}
                  onChange={(checked: boolean) => setFirstTimeTransaction(checked)}
                />
                <Label>First-time customers only</Label>
              </CheckboxField>
            </div>
          </Fieldset>

          {createMutation.isError && (
            <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400">
              {createMutation.error?.message || "Failed to create promo code"}
            </div>
          )}
        </DialogBody>
        <DialogActions>
          <Button plain onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending ? "Creating..." : "Generate Code"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
